/** Inspection Review statuses that indicate the step is finished. */
export const INSPECTION_REVIEW_COMPLETION_STATUSES = [
  "Approved",
  "Approved Conditionally",
  "Completed"
] as const;

export function isInspectionReviewCompletedStatus(
  status?: string | null
): boolean {
  if (!status) return false;
  return INSPECTION_REVIEW_COMPLETION_STATUSES.includes(
    status.trim() as (typeof INSPECTION_REVIEW_COMPLETION_STATUSES)[number]
  );
}

/** Advance draws with no inspection keep budget reconcile disabled until inspection review completes. */
export function shouldDisableBudgetReconcileForIncompleteInspection(
  inspectionReviewStatus?: string | null
): boolean {
  return !isInspectionReviewCompletedStatus(inspectionReviewStatus);
}

export type LoanDrawReconcileSummary = {
  drawNumber?: string | number;
  inspectionReviewStatus?: string;
};

/** Loan draws keyed by drawId (runtime shape from drawDiscussions store). */
export type DrawDiscussionsByDrawId = Record<
  string,
  { drawDetails?: LoanDrawReconcileSummary }
>;

/** Normalizes drawDiscussions from Redux (typed as array, stored as drawId map). */
export function selectDrawDiscussionsByDrawId(
  drawDiscussions: unknown
): DrawDiscussionsByDrawId {
  if (
    !drawDiscussions ||
    typeof drawDiscussions !== "object" ||
    Array.isArray(drawDiscussions)
  ) {
    return {};
  }
  return drawDiscussions as DrawDiscussionsByDrawId;
}

export function getLoanDrawsFromDiscussions(
  drawDiscussionsHistory?: DrawDiscussionsByDrawId | null
): LoanDrawReconcileSummary[] {
  if (
    !drawDiscussionsHistory ||
    typeof drawDiscussionsHistory !== "object" ||
    Array.isArray(drawDiscussionsHistory)
  ) {
    return [];
  }
  return Object.values(drawDiscussionsHistory)
    .map((entry) => entry?.drawDetails)
    .filter((draw): draw is LoanDrawReconcileSummary => Boolean(draw));
}

/**
 * Lock Budget Reconcile on the current draw when any later draw (higher drawNumber)
 * has Inspection Review in a completion status.
 */
export function shouldLockPastDrawBudgetReconcile(
  currentDrawNumber: string | number | undefined,
  loanDraws: LoanDrawReconcileSummary[]
): boolean {
  const currentNum = Number(currentDrawNumber);
  if (Number.isNaN(currentNum)) return false;

  return loanDraws.some((draw) => {
    const drawNum = Number(draw.drawNumber);
    if (Number.isNaN(drawNum) || drawNum <= currentNum) return false;
    return isInspectionReviewCompletedStatus(draw.inspectionReviewStatus);
  });
}
